/**
 * @summary - Component that Provides CustomTextField functionality
 * @author vengateshwaran.k 
 * @since 2024-05-28
 */
import {
  FormHelperText,
  TextField,
  Typography
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { InputField } from "../../assets/styled-components/textField";

const CustomTextField = ({
  disabled = false,
  label,
  onMouseLeave,
  isRefilByOtherField = false,
  id,
  autoFocus,
  onChange,
  value,
  isMandatoryField = false,
  placeholder,
  onlyNumbers = false,
  maxlength = 50,
  error,
  errorMessage,
  helperText,
  inputRef,
  name,
  readOnly = false,
  allowDecimal = false,  // New attribute
  checkDuplicate = false,
  valueForCheckDuplicate = [],
  isDuplicate,
  multiLine = false,
  numbersLength = 4,
  textAlign = 'left'

}) => {
  const mandatoryRequiredButton = useSelector(
    (state) => state.mandatoryFields.isMandatoryRequiredEnabled
  );
  const viewEnabled = useSelector(
    (state) => state.mandatoryFields.isViewEnabled
  );
  const maxLength = onlyNumbers ? numbersLength : maxlength;
  const [duplicate, setDuplicate] = React.useState(false)
  const [duplicateMessage, setDuplicateMessage] = React.useState('');

  const handleInputChange = (e) => {
    setDuplicate(false);
    if (checkDuplicate === true) {
      isDuplicate(false);
    }
    let inputValue = e.target.value;

    // If onlyNumbers or allowDecimal is true, filter out non-numeric characters
    if (onlyNumbers || allowDecimal) {
      // Replace any non-numeric characters except decimal point if allowDecimal is true
      const regex = allowDecimal ? /[^0-9.]/g : /[^0-9]/g;
      inputValue = inputValue.replace(regex, "");
    }

    // Call the provided onChange handler with the filtered input value
    onChange(inputValue, name, isMandatoryField);
  };

  const handleBlur = () => {
    if (allowDecimal || onlyNumbers) {
      value = String(value); // Convert to string safely
    }
    if (value && allowDecimal && !value?.includes(".")) {
      // Append .00 to whole numbers
      onChange(value + ".00", name, isMandatoryField);
    } else if (value && allowDecimal && value?.includes(".")) {
      const [integerPart, decimalPart] = value?.split(".");
      // Ensure there are two decimal places
      const updatedValue = decimalPart.length < 2
        ? `${integerPart}.${decimalPart.padEnd(2, "0")}`
        : value;
      onChange(updatedValue, name, isMandatoryField);
    } else if (value && checkDuplicate) {

      const findDuplicate = valueForCheckDuplicate?.map((item) => {
        if (item.itemcode === value) {
          setDuplicate(true);
          isDuplicate(true);
        }
      })


    }
  };

  const handleKeyDown = (e) => {
    // Allow specific key codes related to numbers (0-9) and navigation (arrows, backspace, delete)
    const allowedKeyCodes = [
      8, // Backspace
      9, // Tab
      37, // Left Arrow
      39, // Right Arrow
      46, // Delete
      48,
      49,
      50,
      51,
      52,
      53,
      54,
      55,
      56,
      57, // Digits 0-9
      96,
      97,
      98,
      99,
      100,
      101,
      102,
      103,
      104,
      105, // Numpad 0-9
      190 // Dot (.)
    ];

    // Check if onlyNumbers or allowDecimal is true and the pressed key is not allowed
    if ((onlyNumbers || allowDecimal) && !allowedKeyCodes.includes(e.keyCode)) {
      // Prevent default behavior (e.g., typing non-numeric characters)
      e.preventDefault();
    }
  };

  // Determine the placeholder text
  const placeholderText = placeholder !== undefined ? placeholder : onlyNumbers ? "Enter Number" : "Please Enter";

  return (
    <>
      <Typography variant="labelSm" color={"text.primary"} component={"label"}>
        {label}
        {isMandatoryField && (
          <Typography component={"span"} color={"error"}>
            *
          </Typography>
        )}
      </Typography>
      <TextField
        autoFocus={autoFocus}
        onMouseLeave={onMouseLeave}
        value={value}
        id={id}
        fullWidth
        variant="outlined"
        placeholder={(!readOnly && !disabled) ? placeholderText : ""}
        onChange={handleInputChange}
        theme={InputField}
        onBlur={handleBlur} // Trigger blur event
        onKeyDown={handleKeyDown} // Intercept keyboard events
        disabled={
          disabled ||
          viewEnabled ||
          (mandatoryRequiredButton && !isMandatoryField) ||
          isRefilByOtherField
        } // Set disabled state based on the 'disabled' variable
        required={isMandatoryField}
        error={error}
        inputProps={{ maxLength, readOnly, style: { textAlign: textAlign } }}
        helperText={helperText || errorMessage}
        inputRef={inputRef}
        name={name}
        multiline={multiLine}
      />
    </>
  );
};

export default CustomTextField;
